import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatusForId as getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { Group, Groups, YammerState } from '../../state/types';
import { isFeatureRolloutEnabled } from '../featureRollouts/selectors';
import { createFindById, createFindByIdThrows } from '../findById';
import { isNetworkQuestionsEnabledAndLicensed } from '../networkQuestions/selectors';
import { getCurrentSessionUser } from '../session/selectors';
import { getCurrentUser } from '../users/selectors';
import { isAdmin as isGroupAdmin } from '../viewerCommunityRelationships/selectors';

export const AllCompanyGroupId = 'eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIwIn0';

/**
 * @deprecated This causes performance issues by triggering re-renders whenever the entity gets updated.
 * Use **findGroupField** instead.
 */
export const findById = createFindById('groups');
/**
 * @deprecated This causes performance issues by triggering re-renders whenever the entity gets updated.
 * Use **findGroupFieldThrows** instead.
 */
export const findByIdThrows = createFindByIdThrows('groups');

type FindGroupField = <T extends keyof Group>(
  state: YammerState,
  groupId: Group['id'],
  fieldName: T
) => Group[T] | undefined;
export const findGroupField: FindGroupField = (state, groupId, fieldName) => {
  const group = findById(state, groupId);
  if (!group) {
    return undefined;
  }

  return group[fieldName];
};

type FindGroupFieldThrows = <T extends keyof Group>(state: YammerState, groupId: Group['id'], fieldName: T) => Group[T];
export const findGroupFieldThrows: FindGroupFieldThrows = (state, groupId, fieldName) => {
  const group = findByIdThrows(state, groupId);

  return group[fieldName];
};

type GroupBooleanSelector = (state: YammerState, groupId: Group['id']) => boolean;

type GetStatusForId = (state: YammerState, groupId: Group['id']) => EntityStatus;
export const getStatusForId: GetStatusForId = (state, groupId) => getStatus(state.groups, groupId);

type IsFulfilled = (state: YammerState, groupId: Group['id']) => boolean;
export const isFulfilled: IsFulfilled = (state, groupId) => getStatus(state.groups, groupId) === 'fulfilled';

export const isExternal: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return !!(group && group.isExternal);
};

export const isMultiTenantOrganizationGroup: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return group?.isMultiTenantOrganizationGroup ?? false;
};

export const isOfficial: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return group?.isOfficial ?? false;
};

export const isLegacyAllCompany: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return !!(group && group.id === AllCompanyGroupId);
};

export const isConnected: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return group ? !!group.officeUnifiedGroupId : false;
};

type IsForeignNetworkGroup = (state: YammerState, groupId: Group['id']) => boolean;
export const isForeignNetworkGroup: IsForeignNetworkGroup = (state, groupId) => {
  const group = findById(state, groupId);
  const { networkId } = getCurrentUser(state);

  return group ? group.networkId !== networkId : false;
};

type GetGroupsState = (state: YammerState) => Groups;
export const getGroupsState: GetGroupsState = (state) => state.groups;

type GetFulfilledGroups = (state: YammerState) => Record<string, Group>;
export const getFulfilledGroups: GetFulfilledGroups = (state) => state.groups.fulfilled.byId;

type GetFulfilledGroup = (state: YammerState, groupId?: Group['id']) => Group | undefined;
/**
 * @deprecated PERFORMANCE This returns an entity object and causes unnecessary re-renders. Use `findGroupField` instead.
 */
export const getFulfilledGroup: GetFulfilledGroup = (state, groupId) => {
  if (!groupId) {
    return;
  }

  return findById(state, groupId);
};

export const canEditSettings: GroupBooleanSelector = (state, groupId) => {
  const { isVerifiedNetworkAdmin } = getCurrentSessionUser(state);
  if (isVerifiedNetworkAdmin) {
    return true;
  }

  return isGroupAdmin(state, groupId);
};

export const canCreateEvent: GroupBooleanSelector = (state, groupId) => {
  if (isLegacyAllCompany(state, groupId)) {
    return false;
  }
  const isAdmin = isGroupAdmin(state, groupId);
  const isGroupConnected = isConnected(state, groupId);
  const isCreateFeatureEnabled = isFeatureRolloutEnabled(state, 'EventsCreation');

  return isCreateFeatureEnabled && isAdmin && isGroupConnected;
};

type GetGroupDatabaseId = (state: YammerState, groupId: Group['id']) => string;
export const getGroupDatabaseId: GetGroupDatabaseId = (state, groupId) => findByIdThrows(state, groupId).databaseId;

export const hasGuestsInGroup: GroupBooleanSelector = (state, groupId) => {
  const group = findById(state, groupId);

  return !!(group && group.guestsCount > 0);
};

type GetGroupTelemetryId = (state: YammerState, groupId?: Group['id']) => string | undefined;
export const getGroupTelemetryId: GetGroupTelemetryId = (state, groupId) =>
  groupId ? findById(state, groupId)?.telemetryId : undefined;

type IsMoveThreadToThisGroupRestricted = (state: YammerState, groupId: Group['id'] | null | undefined) => boolean;
export const isMoveThreadToThisGroupRestricted: IsMoveThreadToThisGroupRestricted = (state, groupId) => {
  if (!groupId) {
    return false;
  }

  const group = findById(state, groupId);
  if (!group) {
    return false;
  }

  return group.isMoveThreadToThisGroupRestricted;
};

type IsNetworkQuestionGroup = (state: YammerState, groupId: Group['id'] | null | undefined) => boolean;
export const isNetworkQuestionGroup: IsNetworkQuestionGroup = (state, groupId) =>
  !!groupId && isNetworkQuestionsEnabledAndLicensed(state);

interface GroupTelemetryEventProperties {
  readonly groupId?: Group['telemetryId'];
  readonly isAnswersCommunity?: boolean;
}

type GetGroupTelemetryEventProperties = (
  state: YammerState,
  groupId: Group['id'] | undefined
) => GroupTelemetryEventProperties;
export const getGroupTelemetryEventProperties: GetGroupTelemetryEventProperties = createCachedSelector(
  getGroupTelemetryId,
  isNetworkQuestionGroup,
  (_state, groupId) => groupId,
  (groupTelemetryId, isAnswersCommunity, groupId) => {
    if (groupId == null) {
      return {};
    }

    return {
      groupId: groupTelemetryId,
      isAnswersCommunity,
    };
  }
)((_state, groupId) => groupId ?? '');
